import * as React from "react";
import { Button } from "../inputs/button";
import { cn } from "../utils";
import { TruncatedValue } from "../data-display/truncated-value";

export interface ArtifactThumbnailCardProps {
  /**
   * Already-rendered thumbnail node. Callers compose this from whatever
   * upload/preview component their app uses (PDF preview, image, generic
   * file icon, etc.). The card just frames it in a square.
   */
  thumbnail: React.ReactNode;
  /**
   * Display filename. Callers should pass a clean, human-readable name
   * (e.g. via `displayFileNameFromObjectKey` from `@quovy/types`) — the
   * card never derives it from a storage key.
   */
  fileName: string;
  /**
   * Optional leading slot next to the filename. Typically a content-type
   * badge such as `<FileTypeBadge />`.
   */
  fileTypeBadge?: React.ReactNode;
  /**
   * Optional footer row. Typically a `<StatusBadge />` plus a trailing
   * meta label (artifact kind, etc.). Caller controls semantics.
   */
  footer?: React.ReactNode;
  /**
   * Optional hover-action slot — rendered as an absolutely-positioned
   * overlay in the top-right corner of the thumbnail, revealed on hover or
   * focus-within. Typically a `<DropdownMenu>` with a Download item. Kept a
   * sibling of the click target (never nested inside the card's `<button>`)
   * so an interactive menu is not invalid-nested inside a button.
   */
  actions?: React.ReactNode;
  /** Optional click handler — when provided, the card becomes a button. */
  onClick?: () => void;
  /** Selection state for keyboard/click-driven pickers. */
  selected?: boolean;
  className?: string;
}

/**
 * Pure presentational card for an uploaded artifact thumbnail. Frames a
 * square thumbnail above a filename row and an optional footer row.
 *
 * Does no data fetching, no filename derivation, no extraction-status
 * mapping — callers compose those in. See `submissions-page.tsx` for a
 * reference orchestration wrapper.
 *
 * When `onClick` is provided the card's content becomes a `<button>`; the
 * `actions` overlay is rendered as a sibling of that button (never inside
 * it) so a nested interactive menu stays valid HTML and keeps its own
 * pointer/keyboard handling.
 */
export const ArtifactThumbnailCard = React.forwardRef<HTMLDivElement, ArtifactThumbnailCardProps>(
  function ArtifactThumbnailCard(
    { thumbnail, fileName, fileTypeBadge, footer, actions, onClick, selected, className },
    ref,
  ) {
    const interactive = typeof onClick === "function";

    const content = (
      <>
        <div className="flex aspect-square w-full min-w-0 items-center justify-center overflow-hidden rounded border bg-muted/30">
          {thumbnail}
        </div>
        {/*
         * `w-full` matters: inside the interactive branch these rows are flex
         * children of a centering Button, so without it they'd size to their
         * content and a long filename would overflow the card on both sides.
         */}
        <div className="flex w-full min-w-0 items-center gap-1.5">
          {fileTypeBadge}
          <TruncatedValue className="min-w-0 flex-1 text-sm" fullText={fileName}>
            {fileName}
          </TruncatedValue>
        </div>
        {footer ? (
          <div className="flex w-full items-center justify-between gap-2">{footer}</div>
        ) : null}
      </>
    );

    // The actions overlay sits over the top-right of the thumbnail and is
    // revealed on group hover, focus-within (keyboard), or whenever its own
    // menu is open (`data-state="open"` on the trigger). It is a sibling of
    // the click target, not a child, so it never nests inside the `<button>`.
    const actionsOverlay = actions ? (
      <div
        className="absolute right-1.5 top-1.5 z-10 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 [&:has([data-state=open])]:opacity-100"
        onClick={(event) => event.stopPropagation()}
      >
        {actions}
      </div>
    ) : null;

    const outerClassName = cn(
      "group relative flex w-full min-w-0 flex-col gap-2 overflow-hidden rounded-md border bg-background p-2 transition",
      interactive && [
        "hover:border-muted-foreground/40 hover:bg-muted/40",
        "focus-within:border-muted-foreground/40",
      ],
      selected && "border-primary bg-primary/5 shadow-sm",
      className,
    );

    if (interactive) {
      return (
        <div ref={ref} className={outerClassName}>
          <Button
            type="button"
            variant="ghost"
            size="row"
            onClick={onClick}
            aria-pressed={selected}
            title={fileName}
            className="min-w-0 flex-col gap-2 focus-visible:ring-offset-background"
          >
            {content}
          </Button>
          {actionsOverlay}
        </div>
      );
    }

    return (
      <div ref={ref} title={fileName} className={outerClassName}>
        {content}
        {actionsOverlay}
      </div>
    );
  },
);
